import getConfig from "next/config";
import { func } from "prop-types";
import React, { memo, useEffect, useRef, useState } from "react";
import { useToggle } from "react-use";
import { twMerge } from "tailwind-merge";

import ErrorBoundary from "@components/shared/ErrorBoundary";
import CloseIcon from "@components/shared/Icons/Close";
import Icon from "@components/shared/Icons/Icon";
import Switch from "@components/shared/Switch";

import gtm from "@gtm/core";
import { changeModelViewEvent } from "@gtm/events/homepage";

import { getStaticAssetURL } from "@lib/getStaticAssetURL";
import { useTranslation } from "@lib/i18n";

import { useActiveCarPreconfigurationSelector } from "@redux/reducers/deal";

const { publicRuntimeConfig } = getConfig();
const { BUSINESS_ENV } = publicRuntimeConfig;

const WIDGET_STATE = {
  INITIAL: "INITIAL",
  LOADING: "LOADING",
  LOADED: "LOADED",
};

const WIDGET_EVENT = {
  LOADING_START: "360HasStartedLoading",
  LOADING_COMPLETE: "360HasCompletedLoading",
};

const WIDGET_ID = "360Widget";

const ThreeSixty = ({ onClose }) => {
  const [widgetState, setWidgetState] = useState(WIDGET_STATE.INITIAL);
  const [exterior, toggleExterior] = useToggle(true);

  const { t } = useTranslation();
  const activePreConfiguration = useActiveCarPreconfigurationSelector();

  const widgetRef = useRef(null);

  const setViewpoint = () => {
    widgetRef.current?.contentWindow?.setViewpoint?.(exterior);
  };

  const handleExteriorToggle = (overrideValue = null) => {
    if (overrideValue !== null) {
      toggleExterior(overrideValue);
    } else {
      toggleExterior();
    }

    gtm.fire(
      changeModelViewEvent({
        action: exterior ? "interieur" : "exterieur",
        label: t(`carGallery.toggleView.${exterior ? "interior" : "exterior"}`),
      })
    );
  };

  const setPreconfiguration = () => {
    if (widgetState !== WIDGET_STATE.LOADED) {
      BUSINESS_ENV !== "production" &&
        console.warn(
          `Attempted to change configuration before 360 has finished loading. This could cause an error!`
        );
      return;
    }
    widgetRef.current?.contentWindow?.setPreconfiguration?.(
      activePreConfiguration.id360View
    );
  };

  useEffect(() => {
    const handleLoadingStart = () => {
      setWidgetState(WIDGET_STATE.LOADING);
    };

    const handleLoadingComplete = () => {
      setWidgetState(WIDGET_STATE.LOADED);
    };

    window.document.addEventListener(
      WIDGET_EVENT.LOADING_START,
      handleLoadingStart,
      false
    );
    window.document.addEventListener(
      WIDGET_EVENT.LOADING_COMPLETE,
      handleLoadingComplete,
      false
    );

    return () => {
      window.document.removeEventListener(
        WIDGET_EVENT.LOADING_START,
        handleLoadingStart,
        false
      );
      window.document.removeEventListener(
        WIDGET_EVENT.LOADING_COMPLETE,
        handleLoadingComplete,
        false
      );
    };
  }, []);

  useEffect(() => {
    if (widgetState === WIDGET_STATE.LOADED) {
      setPreconfiguration();
    }
  }, [widgetState]);

  useEffect(() => {
    if (widgetState === WIDGET_STATE.LOADED) {
      setViewpoint();
    }
  }, [exterior]);

  return (
    <div className="flex flex-col justify-between w-[88svw] h-full max-w-160 max-h-140 relative overflow-hidden md:size-full">
      <h2
        className={twMerge(
          "w-full px-4 uppercase whitespace-nowrap z-2 text-3xl text-primaryDark text-center font-extrabold mt-4",
          !exterior && "text-white/90"
        )}
      >
        {activePreConfiguration.label ?? ""}
      </h2>

      <button
        aria-label={t("basket.back")}
        className={twMerge(
          "grid place-items-center size-6",
          "border-2 border-greyLight rounded-sm",
          "absolute top-4 right-4 z-2",
          "transition delay-150 hover:scale-110 active:scale-90",
          "hover:border-primaryOrange",
          "cursor-pointer lg:size-8",
          !exterior && "bg-white/90"
        )}
        onClick={onClose}
      >
        <Icon
          className="size-2 lg:size-4"
          icon={<CloseIcon data-id="360-close-icon" />}
        />
      </button>

      <ErrorBoundary id="frame">
        <iframe
          allowFullScreen
          className="size-full absolute inset-0 border-none"
          id={WIDGET_ID}
          name={WIDGET_ID}
          onmousewheel=""
          ref={widgetRef}
          // Don't use CDN, so that the Same-origin policy
          // does not prevent js access to the iframe
          src={getStaticAssetURL("/360/index.html", { useCdn: false })}
        />
      </ErrorBoundary>

      {widgetState === WIDGET_STATE.LOADED && (
        <div
          className={twMerge(
            "flex justify-center items-center self-center gap-6 px-4 mb-4 rounded-full z-2",
            "bg-white/50 text-white"
          )}
          data-id="360-toggle-view-switch-container"
        >
          <span
            className={twMerge(
              "block text-xs font-semibold cursor-pointer",
              exterior ? "text-primaryOrange" : "text-primaryDark"
            )}
            onClick={() => handleExteriorToggle(true)}
          >
            {t("carGallery.toggleView.exterior")}
          </span>
          <Switch
            checked={!exterior}
            data-id="360-toggle-view-button"
            isHorizontal
            onChange={handleExteriorToggle}
            className={{
              body: "bg-white/50 ring-primaryOrange",
              slider: "bg-primaryOrange",
            }}
          />
          <span
            className={twMerge(
              "block text-xs font-semibold cursor-pointer",
              !exterior ? "text-primaryOrange" : "text-primaryDark"
            )}
            onClick={() => handleExteriorToggle(false)}
          >
            {t("carGallery.toggleView.interior")}
          </span>
        </div>
      )}
    </div>
  );
};

ThreeSixty.propTypes = {
  onClose: func,
};

export default memo(ThreeSixty);
